'use client';

import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';

import { apiRoutes, getApiUrl } from '@/constants/api';
import { useIsMobile } from '@/hooks/use-mobile';
import { RateLimitError } from '@/lib/api/errors/api-errors';
import { clientApiFetcher } from '@/lib/api/fetcher/client';

import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

export default function ShareFeedbackButton() {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const cookieValue = getCookie('feedbackSubmitted');
    if (cookieValue === 'true') {
      setHasSubmittedFeedback(true);
    }
  }, []);

  useEffect(() => {
    const toggleVisibility = () => {
      if (typeof window === 'undefined') return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
      );

      const threshold = 200;
      const nearFooter =
        documentHeight > windowHeight &&
        scrollTop + windowHeight >= documentHeight - threshold;
      setIsVisible(!nearFooter);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', toggleVisibility);

      setTimeout(() => {
        toggleVisibility();
      }, 100);

      setTimeout(() => {
        toggleVisibility();
      }, 500);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', toggleVisibility);
      }
    };
  }, []);

  const handleStarClick = (rating: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasSubmittedFeedback && !isSubmitting) {
      setSelectedRating(rating);
    }
  };

  const handleStarHover = (rating: number) => {
    if (!hasSubmittedFeedback && !isSubmitting) {
      setHoveredRating(rating);
    }
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const handleSubmit = async () => {
    if (!selectedRating || isSubmitting || hasSubmittedFeedback) {
      return;
    }

    setIsSubmitting(true);

    try {
      await clientApiFetcher(getApiUrl(apiRoutes.feedback.submit), {
        method: 'POST',
        body: JSON.stringify({ rating: selectedRating }),
      });

      toast.success('Thank you for your feedback!');
      setSelectedRating(0);
      setIsOpen(false);
      setHasSubmittedFeedback(true);
    } catch (error: unknown) {
      let errorMessage = 'Failed to submit feedback. Please try again.';

      if (error instanceof RateLimitError) {
        if (error.daysRemaining !== undefined && error.daysRemaining > 0) {
          errorMessage = `You have already submitted feedback. Please wait ${error.daysRemaining} day${error.daysRemaining === 1 ? '' : 's'} before submitting again.`;
        } else {
          errorMessage =
            'You have already submitted feedback. Please wait 3 days before submitting again.';
        }
        setHasSubmittedFeedback(true);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error('Failed to submit feedback', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldPeek = hasSubmittedFeedback;
  const translateX = shouldPeek
    ? isHovered || isOpen
      ? '0'
      : 'calc(100% - 12px)'
    : '0';

  const handlePopoverOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      const cookieValue = getCookie('feedbackSubmitted');
      setHasSubmittedFeedback(cookieValue === 'true');
    }
    if (!isMobile) {
      setIsHovered(open);
    }
  };

  return (
    <>
      {isVisible && (
        <div
          className='fixed right-0 top-1/2 z-50 animate-fade-in transition-all duration-300'
          style={{
            transform: `translate(${translateX}, -50%)`,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => !isMobile && setIsHovered(false)}
        >
          <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant='default'
                className={`h-auto bg-brand-forest px-3 py-6 text-base shadow-lg transition-all hover:bg-brand-dark ${
                  hasSubmittedFeedback && isMobile ? 'scale-90' : ''
                }`}
                style={{
                  writingMode: 'vertical-lr',
                  textOrientation: 'sideways',
                  transform: 'rotate(180deg)',
                }}
              >
                Feedback
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side='left'
              align='center'
              className='w-72 max-w-[calc(100vw-80px)] p-4'
              sideOffset={8}
            >
              <div className='flex flex-col gap-4'>
                {hasSubmittedFeedback ? (
                  <div className='text-center text-sm text-gray-600'>
                    Thank you for your feedback! You can submit again in 3 days.
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className='mb-2 py-0 text-center font-poppins text-sm font-semibold'>
                        How would you rate your experience?
                      </h3>
                      <div className='flex flex-wrap items-center justify-center gap-2'>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type='button'
                            onClick={(e) => handleStarClick(star, e)}
                            onMouseEnter={() => handleStarHover(star)}
                            onMouseLeave={handleStarLeave}
                            disabled={hasSubmittedFeedback || isSubmitting}
                            className='transition-colors disabled:cursor-not-allowed disabled:opacity-50'
                            aria-label={`Rate ${star} out of 5`}
                          >
                            <Star
                              className={`size-8 ${
                                star <= (hoveredRating || selectedRating)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={handleSubmit}
                      disabled={
                        !selectedRating || isSubmitting || hasSubmittedFeedback
                      }
                      className='w-full'
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </>
                )}

                <div className='border-t pt-3'>
                  <Link
                    href='/contact'
                    onClick={() => setIsOpen(false)}
                    className='block break-words rounded-lg border border-brand-green bg-brand-light px-3 py-2 text-sm text-brand-dark transition-colors hover:underline'
                  >
                    Have an issue? Click here to visit our contact form
                  </Link>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </>
  );
}
